import { useEffect, useContext, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

import CreatableSelect from "react-select/creatable";
import ToastDisplay from "../components/ToastDisplay";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Pagination from "../components/Pagination";
import PageLoader from "../components/PageLoader";
import Empty from "../components/Empty";
import FileUpload from "../components/FileUpload";

const Personnel = () => {
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [personnel, setPersonnel] = useState([]);
	const [meta, setMeta] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams({});
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [filtermodal, setFilterModal] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [personnelModal, setPersonnelModal] = useState(false);
	const [errors, setErrors] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [departmentLoading, setDepartmentLoading] = useState(false);

	const [name, setName] = useState("");
	const [personnelNo, setPersonnelNo] = useState("");
	const [rfidNo, setRfidNo] = useState("");
	const [department, setDepartment] = useState({});
	const [msisdn, setMsisdn] = useState("");
	const [photo, setPhoto] = useState(null);
	const [photoPath, setPhotoPath] = useState("");

	const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
	const [searchDepartment, setSearchDepartment] = useState(
		searchParams.get("department") || ""
	);

	const searchFields = [
		{ key: "q", value: searchQ },
		{ key: "department", value: searchDepartment },
	];

	const filterFields = [
		{
			key: "department",
			value: searchDepartment,
			defaultVal: "",
			edit: setSearchDepartment,
		},
	];

	const requiredFields = [
		{ key: "name", value: name },
		{ key: "personnelNo", value: personnelNo },
		{ key: "rfidNo", value: rfidNo },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: { "X-Auth-Token": session },
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setPersonnel(data.personnel);
			setMeta(data.meta);
			setDepartments(
				data.departments.map((dept) => ({
					value: dept.name,
					label: dept.name,
				}))
			);
		}
		setLoading(false);
	};

	const search = (e) => {
		e.preventDefault();
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "") {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
		setFilterModal(false);
	};

	const removeFilter = (e, func, key) => {
		e.preventDefault();
		func("");
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "" && field.key !== key) {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
	};

	const clearFilters = (e) => {
		e.preventDefault();
		setSearchParams({});
		filterFields.map((field) => {
			field.edit(field.defaultVal);
		});
	};

	const closePersonnelModal = () => {
		setPersonnelModal(false);
		setName("");
		setPersonnelNo("");
		setRfidNo("");
		setDepartment({});
		setMsisdn("");
		setPhoto(null);
		setPhotoPath("");
		setErrors([]);
	};

	const isEmpty = (value) => {
		return value === "" || value === null || value === undefined;
	};

	const savePersonnel = async () => {
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				name: name,
				personnel_no: personnelNo,
				id_no: rfidNo,
				department: department?.value || "",
				msisdn: msisdn,
				photo: photoPath,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			closePersonnelModal();
			navigate(`/personnel/${data.personnel_id}`);
		}
		setActionLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		savePersonnel();
	};

	const handlePhoto = (e) => {
		const file = e.target.files[0];
		const newUid = uuidv4();
		const uuid_name = `${newUid}.${file.name.split(".").pop()}`;

		const forUpload = {
			uuid: uuid_name,
			file: file,
		};
		setPhoto(forUpload);
		setPhotoPath(
			`${process.env.REACT_APP_STORAGE_URL}/pisara/personnel/${uuid_name}`
		);
	};

	const createDepartment = async (inputValue) => {
		setDepartmentLoading(true);
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				departments: [inputValue],
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel/departments/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			setDepartments([
				...departments,
				{ value: inputValue, label: inputValue },
			]);
			setDepartment({ value: inputValue, label: inputValue });
		}
		setDepartmentLoading(false);
	};

	useEffect(() => {
		document.title = "Personnel | Pisara Dashboard";
		if (profile) {
			if (!profile.role.personnel_view) {
				navigate("/");
			} else {
				getData();
				if (filterFields.every((field) => isEmpty(field.value))) {
					setShowFilters(false);
				} else {
					setShowFilters(true);
				}
			}
		}
	}, [searchParams, profile]);

	return (
		<>
			<Container className="gx-5">
				<Row className="content-header flex-row-left mb-20">
					<Col>
						<h1 className="text-xl m-0">Personnel</h1>
					</Col>
				</Row>
				<Row className="flex-row-left mb-20">
					<Col className="col-5">
						<Form onSubmit={search}>
							<div className="search-container bg-white flex-row-left">
								<i className="ri-search-line search-icon" />
								<Form.Control
									className="text-sm"
									placeholder="Search personnel"
									value={searchQ}
									onChange={(e) => setSearchQ(e.target.value)}
								/>
								<Button
									variant="light"
									className="filter-btn flex-column"
									onClick={() => setFilterModal(true)}
								>
									<i className="ri-equalizer-3-fill m-0" />
								</Button>
							</div>
						</Form>
					</Col>
					<Col className="col-7 flex-row-right">
						<Pagination
							meta={meta}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
							searchFields={searchFields}
							className="pull-right flex-row-left"
						/>
						<Button
							variant="light"
							className={`refresh-btn flex-column ${
								!profile?.role?.personnel_write && "no-margin"
							}`}
							onClick={getData}
						>
							<i className="ri-restart-line opacity-7" />
						</Button>
						{profile?.role?.personnel_write && (
							<Button
								className="control-btn btn-sm btn-default text-medium-gotham text-sm"
								onClick={() => setPersonnelModal(true)}
							>
								<i className="ri-user-add-line" />
								Add Personnel
							</Button>
						)}
					</Col>
				</Row>
				<Row className="content-table-header bg-white gx-0">
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Name</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Personnel No.
						</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							RFID No.
						</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Department
						</span>
					</Col>
				</Row>
				{loading ? (
					<PageLoader className="bg-white p-50" />
				) : personnel?.length === 0 ? (
					<Empty label="No personnel to show." className="bg-white" />
				) : (
					personnel.map((personnel) => (
						<Link key={personnel.id} to={`/personnel/${personnel.id}`}>
							<Row className="content-table-item bg-white gx-0">
								<Col className="p-10 ellipsis">
									<span className="text-sm">{personnel.name}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{personnel.personnel_no}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{personnel.id_no}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{personnel.department}</span>
								</Col>
							</Row>
						</Link>
					))
				)}
			</Container>
			<Modal
				show={personnelModal}
				className="info-modal admin-content"
				centered
				size="lg"
				onHide={closePersonnelModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">Add Personnel</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-30 full flex-column">
							<div className="file-upload-container">
								{photo && (
									<FileUpload
										file={photo}
										files={photo}
										setFiles={setPhoto}
										filePaths={photoPath}
										setFilePaths={setPhotoPath}
										directory="pisara/personnel"
									/>
								)}
								{!photo && (
									<div
										className="file-btn border rounded-8"
										onClick={() => photoRef.current.click()}
									>
										<div className="overlay" />
										<img
											src="/default-user.png"
											alt=""
											className="default-user-photo"
										/>
									</div>
								)}
								<input
									ref={photoRef}
									type="file"
									className="hidden"
									accept="image/*"
									onChange={handlePhoto}
								/>
							</div>
						</div>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Personnel Information
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Name</Form.Label>
								<Form.Control
									autoFocus
									className={`text-sm form-input ${
										errors.includes("name") && "border-danger"
									}`}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</Col>
						</Row>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Personnel No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("personnelNo") && "border-danger"
									}`}
									value={personnelNo}
									onChange={(e) => setPersonnelNo(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">Department</Form.Label>
								<CreatableSelect
									isClearable
									isDisabled={departmentLoading}
									isLoading={departmentLoading}
									onCreateOption={createDepartment}
									value={department}
									onChange={setDepartment}
									options={departments}
									menuShouldScrollIntoView={false}
									maxMenuHeight={200}
									placeholder=""
									styles={{
										control: (baseStyles, state) => ({
											...baseStyles,
											height: "38px",
											outline: "none",
											borderRadius: 4,
											fontSize: "0.875rem",
											borderColor: state.isFocused
												? "#86b7fe !important"
												: "#dee2e6 !important",
											boxShadow: state.isFocused
												? "0 0 0 .25rem rgba(13, 110, 253, .25)"
												: "none",
										}),
										option: (baseStyles, state) => ({
											...baseStyles,
											fontSize: ".875rem",
											color: "#050911",
											backgroundColor: state.isFocused
												? "#c8deff !important"
												: "#fff",
										}),
									}}
								/>
							</Col>
						</Row>
						<Row className="mb-50">
							<Col>
								<Form.Label className="text-sm">Mobile No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("msisdn") && "border-danger"
									}`}
									value={msisdn}
									onChange={(e) => setMsisdn(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">RFID No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("rfidNo") && "border-danger"
									}`}
									value={rfidNo}
									onChange={(e) => setRfidNo(e.target.value)}
								/>
							</Col>
						</Row>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm text-medium-inter me-2"
							onClick={closePersonnelModal}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							className="btn-default btn-sm action-btn flex-column text-sm text-medium-inter"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Save"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</>
	);
};

export default Personnel;
